import { ChatMessage, normalizeMessages } from "./messageUtils";
import { compareMessages } from "./messageOrder";

const stableId = (message: any): string => {
  const id = String(message?.message_id ?? "");
  return id && !id.startsWith("temp_") ? id : "";
};

const requestId = (message: any): string => String(message?.requestId || message?.request_id || "");

function sameMessage(local: any, stored: any): boolean {
  // An assistant can emit several messages in one request, and its first chunks
  // can still carry the user's message id. Neither may consume the user's row.
  if ((local.user_id === "neo") !== (stored.user_id === "neo")) return false;
  const localId = stableId(local);
  const storedId = stableId(stored);
  if (localId && storedId) return localId === storedId;
  if (String(local.user_id || "") !== String(stored.user_id || "")) return false;
  if (local.user_id !== "neo" && requestId(local) && requestId(stored)) {
    return requestId(local) === requestId(stored);
  }
  return Boolean(local.entry_id && local.entry_id === stored.entry_id);
}

/**
 * Reconcile a full history response with messages already seen on this thread.
 * A GET can start before a send and finish after its confirmation or reply, so
 * absence from that snapshot is never evidence that a message was removed.
 * Callers must pass local messages only for the SAME thread; explicit clears,
 * thread deletion and cancellation continue to use their own reducer actions.
 */
export function mergeMessageHistory(local: any[], history: any[]): ChatMessage[] {
  if (history.length === 0) return local;
  const remaining = [...local];
  const stored = normalizeMessages(history).map(message => {
    const index = remaining.findIndex(candidate => sameMessage(candidate, message));
    if (index === -1) return message;
    const previous = remaining.splice(index, 1)[0];
    // Keep replay/request identities, but let the persisted row confirm the
    // optimistic send or replace a partial assistant reply.
    return {
      ...previous, ...message, pending: false, isStreaming: false,
      ...(message.user_id !== "neo" ? { status: "confirmed" as const } : {}),
    };
  });
  // Local rows are already display messages. Normalizing them again could
  // re-combine workflow steps or drop a stream that still carries a user id.
  return [...stored, ...remaining].sort(compareMessages);
}

/** Match a confirmation to its send; never replace an unrelated queued message. */
export function findPendingUserMessageIndex(messages: any[], incoming: any): number {
  const incomingRequest = requestId(incoming);
  if (incomingRequest) {
    const index = messages.findIndex(m => m.pending && requestId(m) === incomingRequest);
    if (index !== -1) return index;
  }
  const content = (message: any) => String(message.content || "").trim().replace(/\s+/g, " ");
  return messages.findIndex(existing => {
    if (!existing.pending || String(existing.user_id || "") !== String(incoming.user_id || "")) return false;
    if (String(existing.content_type || "MESSAGE") !== String(incoming.content_type || "MESSAGE")) return false;
    if (existing.ai_thread_id != null && incoming.ai_thread_id != null && String(existing.ai_thread_id) !== String(incoming.ai_thread_id)) return false;
    const existingRequest = requestId(existing);
    if (existingRequest && incomingRequest) return existingRequest === incomingRequest;
    if (!incomingRequest && existingRequest) return false;
    if (incoming.content_type === "EXPORT_REQUEST" && !incomingRequest) return false;
    return content(existing) === content(incoming);
  });
}

/** Confirm the user send, even if history or an assistant reply arrived first. */
export function updateUserMessageStatus(messages: any[], payload: {
  request_id?: string;
  message_id?: string | number;
  pending?: boolean;
  status?: ChatMessage["status"];
}): ChatMessage[] {
  const { request_id, message_id, pending, status } = payload;
  let index = request_id ? messages.findIndex(m => m.user_id !== "neo" && requestId(m) === String(request_id)) : -1;
  if (index === -1 && message_id) {
    index = messages.findIndex(m => m.user_id !== "neo" && stableId(m) === String(message_id));
  }
  if (index === -1) return messages;

  const updates = {
    ...(message_id ? { message_id: String(message_id) } : {}),
    ...(pending !== undefined ? { pending } : {}),
    ...(status !== undefined ? { status } : pending === false ? { status: "confirmed" as const } : {}),
  };
  const original = messages[index];
  let confirmed = { ...original, ...updates };
  // A history response may already contain the saved row while the optimistic
  // row still has its temporary id. Prefer the saved body/time and keep the send's
  // request and replay identities, then remove that extra copy.
  const duplicates = new Set<number>();
  if (message_id) {
    messages.forEach((message, i) => {
      if (i !== index && message.user_id !== "neo" && stableId(message) === String(message_id)) {
        confirmed = { ...confirmed, ...message, ...updates, requestId: requestId(original) || requestId(message) };
        duplicates.add(i);
      }
    });
  }
  return messages.flatMap((message, i) => duplicates.has(i) ? [] : [i === index ? confirmed : message]).sort(compareMessages);
}
